import { useState, useEffect } from 'react'
import api from '../services/api'

interface Props {
  isOpen: boolean
  onClose: () => void
  petId: string
  petName: string
}

interface Share {
  id: string
  sharedWithEmail: string
  permission: string
  status: string
  inviteToken: string
}

export default function SharePetModal({ isOpen, onClose, petId, petName }: Props) {
  const [recipient, setRecipient] = useState('')
  const [sendMethod, setSendMethod] = useState<'email' | 'text'>('email')
  const [permission, setPermission] = useState('viewer')
  const [sending, setSending] = useState(false)
  const [shares, setShares] = useState<Share[]>([])
  const [success, setSuccess] = useState(false)

  useEffect(() => {
    if (isOpen) loadShares()
  }, [isOpen, petId])

  if (!isOpen) return null

  async function loadShares() {
    try {
      const res = await api.get(`/api/pets/${petId}/shares`)
      setShares(res.data)
    } catch { /* ignore */ }
  }

  async function sendInvite() {
    setSending(true)
    setSuccess(false)
    try {
      await api.post(`/api/pets/${petId}/share`, { email: recipient, permission })
      setSuccess(true)
      setRecipient('')
      await loadShares()
    } catch { /* ignore */ }
    setSending(false)
  }

  async function revokeShare(shareId: string) {
    try {
      await api.delete(`/api/pets/${petId}/shares/${shareId}`)
      await loadShares()
    } catch { /* ignore */ }
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl font-bold">Share {petName}</h2>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Invite form */}
          <div className="space-y-3 mb-6">
            <div className="flex bg-gray-100 rounded-lg p-1">
              <button
                onClick={() => setSendMethod('email')}
                className={`flex-1 text-sm py-1.5 rounded-md transition-colors ${sendMethod === 'email' ? 'bg-white shadow-sm text-gray-900' : 'text-gray-500'}`}
              >
                Email
              </button>
              <button
                onClick={() => setSendMethod('text')}
                className={`flex-1 text-sm py-1.5 rounded-md transition-colors ${sendMethod === 'text' ? 'bg-white shadow-sm text-gray-900' : 'text-gray-500'}`}
              >
                Text Message
              </button>
            </div>

            <input
              type={sendMethod === 'email' ? 'email' : 'tel'}
              placeholder={sendMethod === 'email' ? 'Email address' : 'Phone number'}
              value={recipient}
              onChange={(e) => setRecipient(e.target.value)}
              className="input-field"
            />

            <div className="flex gap-3">
              <select
                value={permission}
                onChange={(e) => setPermission(e.target.value)}
                className="input-field flex-1"
              >
                <option value="viewer">Can View</option>
                <option value="editor">Can Edit</option>
              </select>
              <button
                onClick={sendInvite}
                disabled={!recipient || sending}
                className="btn-primary flex items-center gap-2"
              >
                {sending && <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />}
                {sending ? 'Sending...' : 'Send Invite'}
              </button>
            </div>

            {success && (
              <div className="flex items-center gap-2 text-green-600 text-sm bg-green-50 p-3 rounded-lg">
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
                Invite sent via {sendMethod === 'email' ? 'email' : 'text message'}!
              </div>
            )}

            <p className="text-xs text-gray-400">
              {sendMethod === 'email' ? 'Invite will be sent via email from Pet Halo' : 'Invite will be sent via text message from Pet Halo'}
            </p>
          </div>

          {/* Current shares */}
          <div>
            <h3 className="font-semibold text-gray-700 mb-3">Shared With</h3>
            {shares.length === 0 ? (
              <p className="text-sm text-gray-400">Not shared with anyone yet</p>
            ) : (
              <div className="space-y-2">
                {shares.map(share => (
                  <div key={share.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div>
                      <p className="text-sm font-medium">{share.sharedWithEmail}</p>
                      <p className="text-xs text-gray-500">
                        {share.permission === 'editor' ? 'Can Edit' : 'View Only'} · {share.status}
                      </p>
                    </div>
                    <button onClick={() => revokeShare(share.id)} className="text-red-400 hover:text-red-600">
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
